import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6 } from "styled-components";
import { hasItems } from "~/features/app/utils";
import { Wrap } from "~/features/ui/wrap";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { type SpecsListSpecViewProps } from "~/features/blocks/inventory/specs-list/spec-list-spec";
import { takeFirst } from "~/features/app/utils";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { isLinkValid } from "~/features/ui/link/utils";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
const layouts = {
  full: {
    "width": "100%"
  },
  large: {
    "@media (min-width: 1024px)": {
      "marginLeft": "auto",
      "marginRight": "auto",
      "width": "75%"
    }
  },
  medium: {
    "@media (min-width: 1024px)": {
      "marginLeft": "auto",
      "marginRight": "auto",
      "width": "50%"
    }
  }
} as const;
type Layout = keyof typeof layouts;
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export type SpecsListBlockViewProps<TSpec = Omit<SpecsListSpecViewProps, "mode" | "theme">> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  columns?: string | null;
  getItemKey?: (spec: TSpec, index: number) => React.Key;
  hasBorder?: boolean | null;
  hasTopDivider?: boolean | null;
  isHighlighted?: boolean | null;
  layout?: string | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  mode?: string | null;
  renderSpec: (spec: TSpec, index: number) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  specs?: TSpec[] | null;
  theme?: string | null;
};
export function SpecsListBlockView<TSpec = Omit<SpecsListSpecViewProps, "mode" | "theme">>({
  anchor,
  columns = "3Columns",
  getItemKey,
  hasBorder = true,
  hasTopDivider = true,
  isHighlighted = false,
  layout = "full",
  link: linkValue,
  mode = "light",
  renderSpec,
  sectionProps,
  specs = [],
  theme = "white"
}: SpecsListBlockViewProps<TSpec>) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const items = specs ?? [];
  const is4Columns = columns === "4Columns";
  const is3Columns = columns === "3Columns";
  const link = takeFirst(linkValue);
  const linkIcon = getPlainLinkIcon(link);
  if (!hasItems(items)) {
    return null;
  }
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[{
    "position": "relative",
    "zIndex": "10",
    "backgroundColor": "var(--bg-primary)"
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, isHighlighted && {
    "backgroundColor": "var(--white-static)"
  }]}>
      <_StyledWrap>
        <_StyledDiv $_css2={[{
        "paddingLeft": "var(--spacing-md)",
        "paddingRight": "var(--spacing-md)",
        "@media (min-width: 1024px)": {
          "paddingLeft": "4rem",
          "paddingRight": "4rem"
        }
      }, layouts[layout as Layout]]}>
          <_StyledDiv2 $_css3={[{
          "position": "relative",
          "zIndex": "10",
          "display": "flex",
          "flexWrap": "wrap",
          "backgroundColor": "var(--bg-primary)"
        }, isHighlighted && {
          "backgroundColor": "var(--white-static)"
        },
        // Each cell draws its right/bottom edge (the container its top/left) so wrapped rows and
        // stacked mobile cells are separated by a single line, matching the design's cell grid.
        hasBorder && {
          "borderLeftWidth": "1px",
          "borderTopWidth": "1px"
        }, hasBorder && themeStyle.border]}>
            {items.map((spec, index) => {
            return <_StyledDiv3 key={getItemKey?.(spec, index) || index} $_css4={[{
              "flex": "1 1 0%",
              "flexShrink": "0",
              "flexBasis": "100%"
            }, is4Columns ? {
              "@media (min-width: 1024px)": {
                "flexBasis": "25%"
              }
            } : is3Columns ? {
              "@media (min-width: 1024px)": {
                "flexBasis": "33.333333%"
              }
            } : {
              "@media (min-width: 1024px)": {
                "flexBasis": "50%"
              }
            }, hasBorder && {
              "borderBottomWidth": "1px",
              "borderRightWidth": "1px"
            }, hasBorder && themeStyle.border]}>
                  {renderSpec(spec, index)}
                </_StyledDiv3>;
          })}
          </_StyledDiv2>
          {isLinkValid(link) && <_StyledDiv4 $_css5={[{
          "display": "flex",
          "justifyContent": "center",
          "paddingTop": "4rem",
          "paddingBottom": "4rem",
          "@media (min-width: 1024px)": {
            "paddingTop": "6.4rem",
            "paddingBottom": "6.4rem"
          }
        }, themeStyle.text]}>
              <Button as={Link} {...link} variant="underline" icon={linkIcon}>
                {(link as any).text}
              </Button>
            </_StyledDiv4>}
        </_StyledDiv>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-g46a8i-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-g46a8i-1"
})({
  "borderWidth": "0px"
});
var _StyledDiv = _styled3("div").withConfig({
  componentId: "sc-sf-g46a8i-2"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-g46a8i-3"
})`${p => p.$_css3}`;
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-g46a8i-4"
})`${p => p.$_css4}`;
var _StyledDiv4 = /*#__PURE__*/_styled6("div").withConfig({
  displayName: "specs-list___StyledDiv4",
  componentId: "sc-1rhylwq-0"
})(["", ""], p => p.$_css5);